import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setFilters, clearFilters } from '../ducks/slice';
import { RequestStatus, OnboardingType } from '../constants';

const STORAGE_KEY = 'tdsp-requests-filters';

interface Filters {
  status: string;
  onboardingType: string;
  requestNumber: string;
  raisedBy: string;
  raisedFor: string;
}

const defaultFilters: Filters = {
  status: RequestStatus.ALL,
  onboardingType: OnboardingType.ALL,
  requestNumber: '',
  raisedBy: '',
  raisedFor: '',
};

export const useRequestsFilters = () => {
  const dispatch = useDispatch();

  // Load persisted filters from localStorage
  const getPersistedFilters = useCallback((): Filters => {
    try {
      const savedFilters = localStorage.getItem(STORAGE_KEY);
      if (savedFilters) {
        return JSON.parse(savedFilters);
      }
    } catch (error) {
      console.error('Error loading persisted filters:', error);
    }
    return defaultFilters;
  }, []);

  const [localFilters, setLocalFilters] = useState<Filters>(getPersistedFilters);

  const handleFilterChange = useCallback((filterName: string, value: string) => {
    setLocalFilters((prev) => ({ ...prev, [filterName]: value }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    // Save filters to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(localFilters));
    } catch (error) {
      console.error('Error saving filters:', error);
    }
    dispatch(setFilters(localFilters));
  }, [localFilters, dispatch]);

  const handleClearFilters = useCallback(() => {
    // Clear from localStorage
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing filters:', error);
    }
    setLocalFilters(defaultFilters);
    dispatch(clearFilters());
  }, [dispatch]);

  return {
    localFilters,
    getPersistedFilters,
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters,
  };
};
